import { StyleSheet } from '@react-pdf/renderer'
import { Style } from '@react-pdf/types'
import { defaultFormPdfFontFamily, pdfDefaultFontSize } from './PDFCommon'

/** Base styles of PDF form tables, the styles below build on top of them */
export const pdfTableStyles = StyleSheet.create({
  table: {
    // @ts-expect-error
    display: 'table',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0
  },
  tableRow: {
    margin: 'auto',
    flexDirection: 'row'
  },
  tableCol: {
    borderStyle: 'solid',
    minHeight: 16,
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 4,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  tableCell: {
    fontFamily: defaultFormPdfFontFamily,
    fontSize: 10,
    alignItems: 'flex-start',
    justifyContent: 'flex-start'
  },
  secondLevelRowCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start'
  },
  mainTableCell: {
    fontFamily: defaultFormPdfFontFamily,
    fontSize: 10,
    fontWeight: 'bold',
    padding: 2
  },
  helptextCell: {
    fontFamily: defaultFormPdfFontFamily,
    fontSize: 8,
    fontStyle: 'italic',
    margin: '0 2px'
  },
  elementTitle: {
    fontFamily: defaultFormPdfFontFamily,
    fontSize: 10,
    fontWeight: 'bold'
  },
  title: {
    padding: 10,
    fontSize: 16,
    fontWeight: 400
  }
})

export interface PdfTableHeaderConfig {
  pdfHeaderTextProps?: string[]
  pdfBackgroundColor?: { id: string }
  pdfFontSize?: string | number
}

/** Common styles of every PDF form table column */
export const pdfTableColStyle: Style = {
  ...pdfTableStyles.tableCol,
  flexGrow: 1,
  flexShrink: 1,
  alignSelf: 'stretch',
  justifyContent: 'flex-start'
}

/** Common styles of every PDF form table row */
export const pdfTableRowStyle: Style = {
  ...pdfTableStyles.tableRow,
  flexGrow: 1,
  alignItems: 'flex-start'
}

// Font family is left out of text styles, so the font picked for the language in FormPdf
// on Document and Page is inherited. The shared base styles hardcode the default font
const { fontFamily: _cellFont, ...tableCellWithoutFont } = pdfTableStyles.tableCell
const { fontFamily: _boldFont, ...mainTableCellWithoutFont } = pdfTableStyles.mainTableCell
const { fontFamily: _helpTextFont, ...helptextCellWithoutFont } = pdfTableStyles.helptextCell

/** Text style of table body cells. Set on the cell, it's inherited by the texts inside */
export const pdfTableCellTextStyle: Style = tableCellWithoutFont

/** Bold text inside body cells, e.g. section titles and sums */
export const pdfTableBoldTextStyle: Style = mainTableCellWithoutFont

/** Small italic help text inside body cells */
export const pdfTableHelpTextStyle: Style = helptextCellWithoutFont

/**
 * Builds the style of table header cells.
 *
 * @param config - Header options from the type props, defaults are used when omitted
 * @returns Header cell style, text properties are inherited by the label
 */
export const getPdfTableHeaderStyle = (config: PdfTableHeaderConfig = {}): Style => {
  const { pdfHeaderTextProps = [], pdfBackgroundColor, pdfFontSize } = config
  const style: Style = {
    ...pdfTableCellTextStyle,
    fontSize: pdfFontSize ? +pdfFontSize : pdfDefaultFontSize,
    fontWeight: 'bold'
  }
  if (pdfHeaderTextProps.includes('italics')) {
    style.fontStyle = 'italic'
  }
  if (pdfHeaderTextProps.includes('underline')) {
    style.textDecoration = 'underline'
  }
  if (pdfBackgroundColor) {
    style.backgroundColor = pdfBackgroundColor.id
  }
  return style
}
